import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { cn } from "@volca/ui/lib/utils";

interface AppAsideContextValue {
  slot: HTMLElement | null;
  setSlot: (slot: HTMLElement | null) => void;
  openCount: number;
  setOpenCount: (update: (count: number) => number) => void;
  /** What the open aside holds, for its landmark's name. */
  label: string;
  setLabel: (label: string) => void;
}

const AppAsideContext = createContext<AppAsideContextValue | null>(null);

const DEFAULT_LABEL = "Assistant";

export const AppAsideProvider = ({ children }: { children: ReactNode }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [openCount, setOpenCount] = useState(0);
  const [label, setLabel] = useState(DEFAULT_LABEL);
  const value = useMemo(
    () => ({ slot, setSlot, openCount, setOpenCount, label, setLabel }),
    [slot, openCount, label],
  );
  return (
    <AppAsideContext.Provider value={value}>
      {children}
    </AppAsideContext.Provider>
  );
};

export const AppAsideSlot = () => {
  const context = useContext(AppAsideContext);
  const isOpen = (context?.openCount ?? 0) > 0;
  // Relative, not static: a label taken out of flow stays inside the panel
  // instead of stretching the window.
  return (
    <aside
      ref={context?.setSlot}
      aria-label={context?.label ?? DEFAULT_LABEL}
      className={cn("hidden", {
        "fixed inset-0 z-40 flex flex-col bg-surface md:relative md:z-auto md:my-2 md:mr-2 md:w-[400px] md:shrink-0 md:overflow-hidden md:rounded-xl md:shadow-raised":
          isOpen,
      })}
    />
  );
};

interface AppAsideProps {
  open: boolean;
  children: ReactNode;
  keepMounted?: boolean;
  onClose?: () => void;
  /** Names the aside while it is open: "Assistant", "Job 48218". */
  label?: string;
  /**
   * Cover the page instead of taking a column. A phone always does this.
   * A wide page uses it when docking the panel would crush the page.
   */
  overlay?: boolean;
}

export const AppAside = ({
  open,
  children,
  keepMounted = false,
  onClose,
  label = DEFAULT_LABEL,
  overlay = false,
}: AppAsideProps) => {
  const isMobile = useIsMobile();
  const dialog = useRef<HTMLDialogElement>(null);
  const asDialog = !!onClose && (isMobile || overlay);
  useEffect(() => {
    const element = dialog.current;
    if (!asDialog) return;
    if (open && !element?.open) element?.showModal();
    else if (!open && element?.open) element.close();
    return () => {
      if (element?.open) element.close();
    };
  }, [open, asDialog]);
  const context = useContext(AppAsideContext);
  const setOpenCount = context?.setOpenCount;
  const setLabel = context?.setLabel;

  useEffect(() => {
    if (open && setLabel) setLabel(label);
  }, [open, label, setLabel]);

  useEffect(() => {
    if (!open || !setOpenCount || asDialog) return;
    setOpenCount((count) => count + 1);
    return () => {
      setOpenCount((count) => count - 1);
    };
  }, [open, setOpenCount, asDialog]);

  if (asDialog)
    return (
      <dialog
        ref={dialog}
        aria-label={label}
        onCancel={(event) => {
          event.preventDefault();
          onClose?.();
        }}
        className={cn(
          "fixed m-0 hidden max-h-none flex-col overflow-hidden overscroll-contain bg-surface p-0 text-ink backdrop:bg-blanket open:flex",
          {
            "inset-0 h-dvh w-full max-w-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]":
              isMobile,
            "inset-y-2 right-2 left-auto h-[calc(100dvh-1rem)] w-[400px] rounded-xl border border-line shadow-raised":
              !isMobile,
          },
        )}
      >
        {children}
      </dialog>
    );
  if ((!open && !keepMounted) || !context?.slot) return null;
  return createPortal(
    keepMounted ? (
      <div hidden={!open} className="h-full min-h-0">
        {children}
      </div>
    ) : (
      children
    ),
    context.slot,
  );
};
